/* Copyright (c) 2025 MOCA Systems, Inc.  All rights reserved. */
import { CanvasAngularLand } from '../../../app-data/interfaces/canvas-angular-land';
import { IDestroyOptions } from '@appCanvasCommon/core/DestroyOptions';
import { TicketFontSizes } from '../../../app-data/constants/ticket-font-size';
/**
 * Simple model to track the starting value of our canvasTextScale, then provides helpers to reset it to default
 * or set it to a new value based on Print UI size. This controller is used during the print/export process to
 * temporarily adjust text sizes for better print export, then restore the user's original preference when done.
 */
export class TextSizeController {
	/**
	 * The initial value of canvas text scale when lightbox opened.
	 * This is stored so we can restore the user's preference after printing.
	 * @private
	 */
	private _initialValue!: number;
	/**
	 * Creates a new TextSizeController instance
	 * @param _canvasAngularLand - Angular services for accessing user preferences
	 */
	constructor(private _canvasAngularLand: CanvasAngularLand | null) {}
	/**
	 * Captures the current text scale value from user preferences.
	 * Should be called when the print preview/lightbox is first opened.
	 */
	public setup(): void {
		this._initialValue = this._canvasAngularLand!.userPreferenceStateService.canvasTextScale;
	}
	/**
	 * Sets the canvas text scale to a specific numeric value
	 * @param value - The text scale multiplier to apply
	 */
	public setValue(value: number): void {
		// If value is not valid, do not save because this will persist to localStorage and prevent app from loading...
		// (Value of 0 is considered invalid for this value too)
		if (!value) {
			return;
		}
		this._canvasAngularLand!.userPreferenceStateService.canvasTextScale = value;
	}
	/**
	 * Sets the text scale to the predefined "Small" size.
	 */
	public setSmall(): void {
		this.setValue(TicketFontSizes.Small);
	}
	/**
	 * Sets the text scale to the predefined "Normal" size.
	 */
	public setNormal(): void {
		this.setValue(TicketFontSizes.Normal);
	}
	/**
	 * Sets the text scale to the predefined "Large" size.
	 */
	public setLarge(): void {
		this.setValue(TicketFontSizes.Large);
	}
	/**
	 * Restores the text scale to the value it had when initialize() was called.
	 * Should be called when closing the print preview or after export completes.
	 */
	public reset(): void {
		this.setValue(this._initialValue);
	}
	/**
	 * Cleans up
	 */
	public destroy(opts?: IDestroyOptions): void {
		this._canvasAngularLand = null;
	}
}
